/**
 * The toast queue: a tiny store any code can push to (`toast.success(…)`)
 * and one `ToastRegion` renders. Toasts confirm what just happened; errors
 * that need action belong next to the thing that failed, not in a toast.
 */

/** `success` and `info` get a sage icon; `error` a clay one. */
export type ToastTone = 'success' | 'info' | 'error';

/** One toast. */
export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  /** A request ID to quote to support, shown small under an error. */
  reference?: string;
  /** Milliseconds until it closes itself; 0 keeps it until dismissed. */
  timeoutMs: number;
}

/** What `toast.show()` takes. */
export type ToastInput = Omit<ToastItem, 'id' | 'timeoutMs' | 'tone'> & {
  tone?: ToastTone;
  timeoutMs?: number;
};

/** At most this many toasts show at once; older ones make room. */
export const MAX_TOASTS = 3;

let items: readonly ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) listener();
}

/** Removes a toast. */
function dismiss(id: number): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
  const next = items.filter((item) => item.id !== id);
  if (next.length !== items.length) {
    items = next;
    emit();
  }
}

/**
 * Shows a toast. Success and info close after 5 s, errors after 8 s.
 *
 * @returns The toast's ID, for `toast.dismiss`.
 */
function show(input: ToastInput): number {
  const tone = input.tone ?? 'info';
  const item: ToastItem = {
    ...input,
    id: nextId++,
    tone,
    timeoutMs: input.timeoutMs ?? (tone === 'error' ? 8000 : 5000),
  };
  const kept = items.slice(-(MAX_TOASTS - 1));
  for (const dropped of items.slice(0, items.length - kept.length)) dismiss(dropped.id);
  items = [...kept, item];
  if (item.timeoutMs > 0) {
    timers.set(
      item.id,
      setTimeout(() => {
        dismiss(item.id);
      }, item.timeoutMs),
    );
  }
  emit();
  return item.id;
}

/** Pause a toast's timer while it is hovered or focused, so it can be read. */
function hold(id: number): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
}

/** Restart a held toast's timer. */
function release(id: number): void {
  const item = items.find((candidate) => candidate.id === id);
  if (!item || item.timeoutMs === 0 || timers.has(id)) return;
  timers.set(
    id,
    setTimeout(() => {
      dismiss(id);
    }, item.timeoutMs),
  );
}

/** The toast API. */
export const toast = {
  show,
  success: (title: string, description?: string) =>
    show({ tone: 'success', title, ...(description ? { description } : {}) }),
  info: (title: string, description?: string) =>
    show({ tone: 'info', title, ...(description ? { description } : {}) }),
  error: (title: string, options: { description?: string; reference?: string } = {}) =>
    show({ tone: 'error', title, ...options }),
  dismiss,
  hold,
  release,
  /** The toasts showing now (for the region and tests). */
  current: (): readonly ToastItem[] => items,
  /** Subscribes to changes; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Removes every toast (tests and sign-out). */
  clear(): void {
    for (const item of items) dismiss(item.id);
  },
};
